import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import request from 'utils/request';

import {
  makeSelectBasicUser,
  makeSelectUserToken,
} from 'reducers/selectors/userSelectors';
import {makeSelectShowroomEligibility} from 'reducers/selectors/showroomSelectors';

import {
  SHOWROOM_ADMIN_REVIEW_EVENT,
  SHOWROOM_ADJUST_SEATS,
  SHOWROOM_BATCH_FREE_MEMBER_PASS,
  SHOWROOM_DELETE_CHAT_MESSAGE,
  SHOWROOM_DELETE_EVENT,
  SHOWROOM_FETCH_ACCESS_REQUESTS,
  SHOWROOM_FETCH_CHAT_MESSAGES,
  SHOWROOM_FETCH_ELIGIBILITY,
  SHOWROOM_FETCH_PRICING_PACKAGES,
  SHOWROOM_FETCH_EVENT_DETAIL,
  SHOWROOM_FETCH_EVENTS,
  SHOWROOM_FETCH_MEMBERS,
  SHOWROOM_FETCH_VIEWERS,
  SHOWROOM_PUBLISH_EVENT,
  SHOWROOM_REVIEW_ACCESS_REQUEST,
  SHOWROOM_REVIEW_HOST,
  SHOWROOM_UNPUBLISH_EVENT,
  SHOWROOM_UPDATE_EVENT,
  SHOWROOM_UPDATE_PRICING_PACKAGES,
  adminReviewShowroomEventFailed,
  adminReviewShowroomEventSuccess,
  adjustShowroomSeatsFailed,
  adjustShowroomSeatsSuccess,
  batchShowroomFreeMemberPassFailed,
  batchShowroomFreeMemberPassSuccess,
  deleteShowroomChatMessageFailed,
  deleteShowroomChatMessageSuccess,
  deleteShowroomEventFailed,
  deleteShowroomEventSuccess,
  fetchShowroomAccessRequestsFailed,
  fetchShowroomAccessRequestsSuccess,
  fetchShowroomChatMessagesFailed,
  fetchShowroomChatMessagesSuccess,
  fetchShowroomEligibilityFailed,
  fetchShowroomEligibilitySuccess,
  fetchShowroomPricingPackagesFailed,
  fetchShowroomPricingPackagesSuccess,
  fetchShowroomEventDetail,
  fetchShowroomEventDetailFailed,
  fetchShowroomEventDetailSuccess,
  fetchShowroomEventsFailed,
  fetchShowroomEventsSuccess,
  fetchShowroomMembersFailed,
  fetchShowroomMembersSuccess,
  fetchShowroomViewers,
  fetchShowroomViewersFailed,
  fetchShowroomViewersSuccess,
  publishShowroomEventFailed,
  publishShowroomEventSuccess,
  reviewShowroomAccessRequestFailed,
  reviewShowroomAccessRequestSuccess,
  reviewShowroomHostSuccess,
  reviewShowroomHostFailed,
  unpublishShowroomEventFailed,
  unpublishShowroomEventSuccess,
  updateShowroomEventFailed,
  updateShowroomEventSuccess,
  updateShowroomPricingPackagesFailed,
  updateShowroomPricingPackagesSuccess,
} from 'actions/showroomActions';

import {getQueryString} from 'utils/queryString';

import {
  getAccessRequestsRequestUrl,
  getDeleteChatMessageRequestUrl,
  getReviewAccessRequestUrl,
  getShowroomRolePrefix,
  getUpdateEventRequestUrl,
  getViewersRequestUrl,
} from './showroomSaga.helpers';

function normalizeReviewStatus(status) {
  const map = {
    approve: 'approved',
    reject: 'rejected',
    request_more_info: 'need_more_info',
    suspend: 'suspended',
  };
  return map[status] || status;
}

function normalizePagination(response = {}) {
  const pagination = response.pagination || {};
  let pageNumber = 0;
  if (response.pageNumber != null) {
    pageNumber = response.pageNumber;
  } else if (pagination.pageNumber != null) {
    pageNumber = pagination.pageNumber;
  } else if (pagination.page != null) {
    pageNumber = pagination.page;
  }

  return {
    total: response.total != null ? response.total : pagination.total || 0,
    pageNumber,
    pageSize: response.pageSize || pagination.pageSize,
  };
}

function normalizeEvent(event) {
  if (!event) return event;

  const hostCompany = event.hostCompany || event.hostCompanyId || {};
  const eventType = event.eventType || event.type;
  const startAt = event.startAt || event.startTime;
  const locationCountry = event.locationCountry || event.country;
  const companyName =
    event.companyName ||
    hostCompany.companyName ||
    hostCompany.brandName ||
    hostCompany.name;

  return {
    ...event,
    eventType,
    type: eventType,
    startAt,
    startTime: startAt,
    locationCountry,
    country: locationCountry,
    companyName,
    hostCompany: {
      ...hostCompany,
      name: hostCompany.name || companyName,
    },
  };
}

function normalizeEventPayload(details) {
  const payload = {...details};
  if (payload.startTime && !payload.startAt)
    payload.startAt = payload.startTime;
  if (payload.country && !payload.locationCountry)
    payload.locationCountry = payload.country;
  if (payload.type && !payload.eventType) payload.eventType = payload.type;

  delete payload.startTime;
  delete payload.country;
  delete payload.type;

  return payload;
}

function normalizeMember(member) {
  if (!member) return member;
  const card = member.showroomBusinessCard || {};
  return {
    ...member,
    companyId: member.companyId || member._id,
    status: member.status || member.showroomHostStatus || member.hostStatus,
    showroomHostStatus: member.showroomHostStatus || member.hostStatus,
    country: member.country || card.countryCode,
    appliedAt:
      member.appliedAt ||
      member.showroomHostAppliedAt ||
      member.updatedAt ||
      member.createdAt,
    reviewedAt:
      member.reviewedAt ||
      member.showroomHostApprovedAt ||
      member.showroomHostReviewedAt,
  };
}

function normalizeViewer(viewer) {
  if (!viewer) return viewer;
  const user =
    viewer.userId && typeof viewer.userId === 'object' ? viewer.userId : {};
  const displayName =
    viewer.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.username;

  return {
    ...viewer,
    displayName,
    country:
      viewer.country ||
      viewer.countryCode ||
      user.showroomCountryCode ||
      user.country,
    email: viewer.email || user.email,
    isTestAccount: viewer.isTestAccount || user.isShowroomTestAccount,
  };
}

function normalizeListResponse(
  response = {},
  legacyKey,
  normalizer = (item) => item
) {
  const items = response.items || response[legacyKey] || [];
  return {
    items: items.map(normalizer),
    pagination: normalizePagination(response),
  };
}

// ========================
// Events
// ========================
function* fetchEvents(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const rolePrefix =
    action.viewType || getShowroomRolePrefix(user, eligibility);
  const params = getQueryString(action.filters);
  const requestUrl = `showroom/${rolePrefix}/events?${params}`;

  try {
    const response = yield call(request, requestUrl, authToken, {});
    const {items, pagination} = normalizeListResponse(
      response,
      'events',
      normalizeEvent
    );
    yield put(fetchShowroomEventsSuccess(items, pagination));
  } catch (err) {
    yield put(fetchShowroomEventsFailed(err));
  }
}

function* fetchEventDetail(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const rolePrefix = getShowroomRolePrefix(user, eligibility);
  const requestUrl = `showroom/${rolePrefix}/events/${action.eventId}`;

  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchShowroomEventDetailSuccess(normalizeEvent(response.event)));
  } catch (err) {
    yield put(fetchShowroomEventDetailFailed(err));
  }
}

function* updateEvent(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getUpdateEventRequestUrl(
    user,
    eligibility,
    action.eventId
  );
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify(normalizeEventPayload(action.details)),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateShowroomEventSuccess(normalizeEvent(response.event)));
  } catch (err) {
    yield put(updateShowroomEventFailed(err));
  }
}

// ========================
// Host Applications (admin)
// ========================
function* fetchMembers(action) {
  const authToken = yield select(makeSelectUserToken());
  const params = getQueryString(action.filters);
  const requestUrl = `showroom/admin/members?${params}`;

  try {
    const response = yield call(request, requestUrl, authToken, {});
    const {items, pagination} = normalizeListResponse(
      response,
      'members',
      normalizeMember
    );
    yield put(fetchShowroomMembersSuccess(items, pagination));
  } catch (err) {
    yield put(fetchShowroomMembersFailed(err));
  }
}

// ========================
// Viewers
// ========================
function* fetchViewers(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getViewersRequestUrl(
    user,
    eligibility,
    action.eventId
  );

  try {
    const params = getQueryString(action.filters || {});
    const url = params ? `${requestUrl}?${params}` : requestUrl;
    const response = yield call(request, url, authToken, {});
    const viewers =
      response.viewers || response.records || response.items || [];
    yield put(
      fetchShowroomViewersSuccess(
        viewers.map(normalizeViewer),
        response
      )
    );
  } catch (err) {
    yield put(fetchShowroomViewersFailed(err));
  }
}

// ========================
// Access Requests (host)
// ========================
function* fetchAccessRequests(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getAccessRequestsRequestUrl(
    user,
    eligibility,
    action.eventId
  );

  try {
    const response = yield call(request, requestUrl, authToken, {});
    // Normalize access-request rows to ensure userId is never an object.
    // Backend returns normalized DTO, but add defensive handling for legacy data.
    const accessRequests = (response.approvals || []).map((a) => {
      if (a.userId && typeof a.userId === 'object') {
        const u = a.userId;
        return {
          ...a,
          userId: u._id?.toString() || '',
          user: a.user || {
            id: u._id?.toString() || '',
            name:
              [u.firstName, u.lastName].filter(Boolean).join(' ') ||
              u.username ||
              '',
          },
        };
      }
      return a;
    });
    yield put(fetchShowroomAccessRequestsSuccess(accessRequests));
  } catch (err) {
    yield put(fetchShowroomAccessRequestsFailed(err));
  }
}

function* reviewAccessRequest(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getReviewAccessRequestUrl(
    user,
    eligibility,
    action.eventId,
    action.accessRequestId
  );
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({
      status: normalizeReviewStatus(action.action),
      reviewNote: action.reviewNote,
    }),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(reviewShowroomAccessRequestSuccess(response.approval));
    yield put(fetchShowroomViewers(action.listViewId || action.eventId));
  } catch (err) {
    yield put(reviewShowroomAccessRequestFailed(err));
  }
}

// ========================
// Chat Messages
// ========================
function* fetchChatMessages(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const rolePrefix = getShowroomRolePrefix(user, eligibility);
  const requestUrl = `showroom/${rolePrefix}/events/${action.eventId}/chat/messages`;

  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchShowroomChatMessagesSuccess(response.messages));
  } catch (err) {
    yield put(fetchShowroomChatMessagesFailed(err));
  }
}

function* deleteChatMessage(action) {
  const user = yield select(makeSelectBasicUser());
  const eligibility = yield select(makeSelectShowroomEligibility());
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getDeleteChatMessageRequestUrl(
    user,
    eligibility,
    action.eventId,
    action.messageId
  );
  const requestOptions = {
    method: 'DELETE',
  };

  try {
    yield call(request, requestUrl, authToken, requestOptions);
    yield put(deleteShowroomChatMessageSuccess(action.messageId));
  } catch (err) {
    yield put(deleteShowroomChatMessageFailed(err));
  }
}

// ========================
// Eligibility
// ========================
function* fetchEligibility() {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = 'showroom/me/eligibility';

  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(
      fetchShowroomEligibilitySuccess(response.eligibility || response)
    );
  } catch (err) {
    yield put(fetchShowroomEligibilityFailed(err));
  }
}

function* fetchPricingPackages() {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = 'showroom/pricing-packages';

  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchShowroomPricingPackagesSuccess(response.packages || []));
  } catch (err) {
    yield put(fetchShowroomPricingPackagesFailed(err));
  }
}

function* updatePricingPackages(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = 'showroom/admin/pricing-packages';
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify({packages: action.packages}),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateShowroomPricingPackagesSuccess(response.packages || []));
  } catch (err) {
    yield put(updateShowroomPricingPackagesFailed(err));
  }
}

// ========================
// Admin actions
// ========================
function* publishEvent(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/events/${action.eventId}/publish`;
  const requestOptions = {
    method: 'POST',
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(publishShowroomEventSuccess(normalizeEvent(response.event)));
  } catch (err) {
    yield put(publishShowroomEventFailed(err));
  }
}

function* unpublishEvent(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/events/${action.eventId}/unpublish`;
  const requestOptions = {
    method: 'POST',
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(unpublishShowroomEventSuccess(normalizeEvent(response.event)));
  } catch (err) {
    yield put(unpublishShowroomEventFailed(err));
  }
}

function* deleteEvent(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/events/${action.eventId}`;
  const requestOptions = {
    method: 'DELETE',
  };

  try {
    yield call(request, requestUrl, authToken, requestOptions);
    yield put(deleteShowroomEventSuccess(action.eventId));
  } catch (err) {
    yield put(deleteShowroomEventFailed(err));
  }
}

function* adjustSeats(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/events/${action.eventId}/seats`;
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({seatCapacity: action.seats}),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(adjustShowroomSeatsSuccess(normalizeEvent(response.event)));
  } catch (err) {
    yield put(adjustShowroomSeatsFailed(err));
  }
}

function* adminReviewEvent(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/events/${action.eventId}/review`;
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({
      status: normalizeReviewStatus(action.action),
      reviewNote: action.reviewNote,
    }),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(adminReviewShowroomEventSuccess(normalizeEvent(response.event)));
    // Re-fetch to get fully populated event data
    yield put(fetchShowroomEventDetail(action.eventId));
  } catch (err) {
    yield put(adminReviewShowroomEventFailed(err));
  }
}

function* batchFreeMemberPass(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = 'showroom/admin/passes/batch-free';
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.details),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(batchShowroomFreeMemberPassSuccess(response));
  } catch (err) {
    yield put(batchShowroomFreeMemberPassFailed(err));
  }
}

function* reviewHost(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `showroom/admin/hosts/${action.companyId}/review`;
  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({status: normalizeReviewStatus(action.status)}),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(reviewShowroomHostSuccess(normalizeMember(response.company)));
  } catch (err) {
    yield put(reviewShowroomHostFailed(err));
  }
}

export default function* showroomSagas() {
  yield all([
    takeLatest(SHOWROOM_FETCH_EVENTS, fetchEvents),
    takeLatest(SHOWROOM_FETCH_EVENT_DETAIL, fetchEventDetail),
    takeEvery(SHOWROOM_UPDATE_EVENT, updateEvent),
    takeLatest(SHOWROOM_FETCH_MEMBERS, fetchMembers),
    takeEvery(SHOWROOM_REVIEW_HOST, reviewHost),
    takeLatest(SHOWROOM_FETCH_VIEWERS, fetchViewers),
    takeLatest(SHOWROOM_FETCH_ACCESS_REQUESTS, fetchAccessRequests),
    takeEvery(SHOWROOM_REVIEW_ACCESS_REQUEST, reviewAccessRequest),
    takeLatest(SHOWROOM_FETCH_CHAT_MESSAGES, fetchChatMessages),
    takeEvery(SHOWROOM_DELETE_CHAT_MESSAGE, deleteChatMessage),
    takeLatest(SHOWROOM_FETCH_ELIGIBILITY, fetchEligibility),
    takeLatest(SHOWROOM_FETCH_PRICING_PACKAGES, fetchPricingPackages),
    takeLatest(SHOWROOM_UPDATE_PRICING_PACKAGES, updatePricingPackages),
    takeEvery(SHOWROOM_PUBLISH_EVENT, publishEvent),
    takeEvery(SHOWROOM_UNPUBLISH_EVENT, unpublishEvent),
    takeEvery(SHOWROOM_DELETE_EVENT, deleteEvent),
    takeEvery(SHOWROOM_ADJUST_SEATS, adjustSeats),
    takeEvery(SHOWROOM_ADMIN_REVIEW_EVENT, adminReviewEvent),
    takeLatest(SHOWROOM_BATCH_FREE_MEMBER_PASS, batchFreeMemberPass),
  ]);
}
